/** @typedef {'light' | 'dark' | 'system' | 'dim'} ThemeMode */
/** @typedef {'light' | 'dark' | 'dim'} ResolvedTheme */

export const THEME_STORAGE_KEY = 'hrms-theme-mode';
export const THEME_MODES = /** @type {const} */ (['light', 'dark', 'system', 'dim']);

/** @param {string | null | undefined} value */
export function isThemeMode(value) {
  return THEME_MODES.includes(value);
}

/** @param {string | null | undefined} mode */
export function resolveTheme(mode) {
  const stored = isThemeMode(mode) ? mode : 'system';
  if (stored === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }
  if (stored === 'dim') return 'dim';
  if (stored === 'dark') return 'dark';
  return 'light';
}

/** @returns {ThemeMode} */
export function getStoredThemeMode() {
  if (typeof window === 'undefined') return 'system';
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  return isThemeMode(stored) ? stored : 'system';
}

/**
 * Sync DOM classes/attributes for Tailwind dark: + CSS variables.
 * @param {ResolvedTheme} resolved
 */
function syncDomTheme(resolved) {
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  const isDarkish = resolved === 'dark' || resolved === 'dim';
  root.classList.toggle('dark', isDarkish);
  root.style.colorScheme = resolved === 'light' ? 'light' : 'dark';

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const colors = { light: '#f8fafc', dark: '#0f172a', dim: '#1a2332' };
    meta.setAttribute('content', colors[resolved] || colors.light);
  }
}

/**
 * @param {ThemeMode} mode
 * @param {{ animate?: boolean }} [options]
 * @returns {ResolvedTheme}
 */
export function applyTheme(mode, options = {}) {
  const resolved = resolveTheme(mode);
  const root = document.documentElement;
  root.setAttribute('data-theme-mode', isThemeMode(mode) ? mode : 'system');
  syncDomTheme(resolved);

  if (options.animate) {
    root.classList.add('theme-transition');
    window.setTimeout(() => root.classList.remove('theme-transition'), 320);
  }

  return resolved;
}

/** @param {ThemeMode} mode */
export function persistThemeModeLocally(mode) {
  localStorage.setItem(THEME_STORAGE_KEY, mode);
}
